import { useEffect, useState } from 'react'
import { StudentGetPaging } from 'services/studentService'
import { ExamResultGetList, ExamResultSaveList } from 'services/examResultService'
import { formatDate } from 'lib/utils'
import ConfirmDialog from 'popup/ConfirmDialog'
import { ArrowLeft, Save, Search } from 'lucide-react';
import { useHistory, useParams } from 'react-router-dom';
import { Button } from 'antd';

export default function ExamResultsPage() {
    const params = useParams()
    const router = useHistory()
    // @ts-ignore
    const courseId = params.id as string

    const [students, setStudents] = useState<any[]>([])
    const [filteredStudents, setFilteredStudents] = useState<any[]>([])
    const [examResults, setExamResults] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [formData, setFormData] = useState<Record<string, Record<string, string>>>({})
    const [showConfirm, setShowConfirm] = useState(false)
    const [errors, setErrors] = useState<Record<string, Record<string, string>>>({})
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        fetchData()
    }, [courseId])

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredStudents(students)
        } else {
            const filtered = students.filter(student =>
                student.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
            setFilteredStudents(filtered)
        }
    }, [searchTerm, students])

    const fetchData = async () => {
        try {
            setIsLoading(true)
            setErrors({})
            setSearchTerm('')

            // Fetch students
            const studentsRes = await StudentGetPaging({ courseId })
            setStudents(studentsRes.result.items)
            setFilteredStudents(studentsRes.result.items)

            // Fetch exam results
            const resultsRes = await ExamResultGetList({ courseId })
            setExamResults(resultsRes.result)

            // Initialize form data
            const initialFormData: Record<string, Record<string, string>> = {}
            const initialErrors: Record<string, Record<string, string>> = {}

            studentsRes.result.items.forEach((student: any) => {
                initialFormData[student.id] = {}
                initialErrors[student.id] = {}

                // Find student's results if any
                const studentResult = resultsRes.result.find((r: any) => r.studentId === student.id)
                if (studentResult) {
                    Object.entries(studentResult.results).forEach(([examType, exams]: [string, any]) => {
                        exams.forEach((exam: any) => {
                            initialFormData[student.id][exam.examId] = exam.result?.toString() || ''
                            initialErrors[student.id][exam.examId] = ''
                        })
                    })
                }
            })

            setFormData(initialFormData)
            setErrors(initialErrors)
        } catch (error) {
            console.error('Failed to fetch data:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const validateInput = (value: string): string => {
        if (value === '') return ''
        const num = parseFloat(value)
        if (isNaN(num)) return 'Vui lòng nhập số'
        if (num < 0 || num > 100) return 'Giá trị phải từ 0 đến 100'
        return ''
    }

    const handleResultChange = (studentId: string, examId: string, value: string) => {
        const error = validateInput(value)

        setErrors(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [examId]: error
            }
        }))

        setFormData(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [examId]: value
            }
        }))
    }

    const handleSaveResults = async () => {
        try {
            // Validate all inputs before saving
            let hasErrors = false
            const newErrors: Record<string, Record<string, string>> = {}

            students.forEach(student => {
                newErrors[student.id] = {}
                allExams.forEach(exam => {
                    const value = formData[student.id]?.[exam.examId] || ''
                    const error = validateInput(value)
                    newErrors[student.id][exam.examId] = error
                    if (error) hasErrors = true
                })
            })

            setErrors(newErrors)

            if (hasErrors) {
                alert('Vui lòng kiểm tra lại các giá trị nhập. Tất cả điểm số phải từ 0 đến 100.')
                return
            }

            setIsSaving(true)

            // Prepare data for saving
            const saveData = students.map(student => {
                const studentResults: any = {}

                // Group results by exam type
                examResults.forEach(resultItem => {
                    if (resultItem.studentId === student.id) {
                        Object.entries(resultItem.results).forEach(([examType, exams]: [string, any]) => {
                            studentResults[examType] = exams.map((exam: any) => ({
                                examId: exam.examId,
                                examName: exam.examName,
                                result: formData[student.id]?.[exam.examId] ? parseFloat(formData[student.id][exam.examId]) : null
                            }))
                        })
                    }
                })

                return {
                    studentId: student.id,
                    studentName: student.fullName,
                    results: studentResults
                }
            })

            await ExamResultSaveList(courseId, saveData)
            const currentSearchTerm = searchTerm
            fetchData()
            setSearchTerm(currentSearchTerm)
        } catch (error) {
            console.error('Failed to save results:', error)
            alert('Lưu kết quả thi thất bại. Vui lòng thử lại.')
        } finally {
            setIsSaving(false)
            setShowConfirm(false)
        }
    }

    // Extract unique exams from results
    const allExams: any[] = []
    examResults.forEach(result => {
        Object.values(result.results).forEach((exams: any) => {
            exams.forEach((exam: any) => {
                if (!allExams.some(e => e.examId === exam.examId)) {
                    allExams.push(exam)
                }
            })
        })
    })

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        )
    }

    return (
        <div className="container mx-auto p-4">
            <div className="items-center mb-6">
                <Button
                    onClick={() => router.push(`/courses/schedule/${courseId}`)}
                    className="flex items-center px-4 py-2 mb-5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    <ArrowLeft className="mr-2" />
                    Quay lại khóa học
                </Button>
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-bold text-left flex-grow">QUẢN LÝ KẾT QUẢ THI</h1>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Tìm kiếm học viên..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-gray-100">
                        <tr>
                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">STT</th>
                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">Họ tên</th>
                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">Ngày sinh</th>

                            {allExams.map(exam => (
                                <th key={exam.examId} className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                                    {exam.examName}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {filteredStudents.map((student, index) => (
                            <tr key={student.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{student.fullName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(student.birthDate)}</td>

                                {allExams.map(exam => {
                                    const resultValue = formData[student.id]?.[exam.examId] || ''
                                    const error = errors[student.id]?.[exam.examId] || ''
                                    return (
                                        <td key={`${student.id}-${exam.examId}`} className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={resultValue}
                                                    onChange={(e) => handleResultChange(student.id, exam.examId, e.target.value)}
                                                    className={`w-20 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                                        error ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                    }`}
                                                />
                                                {error && (
                                                    <span className="text-red-500 text-xs mt-1">{error}</span>
                                                )}
                                            </div>
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <Button
                    onClick={() => setShowConfirm(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    disabled={isSaving}
                >
                    <Save className="mr-2" />
                    {isSaving ? 'Đang lưu...' : 'Lưu kết quả'}
                </Button>
            </div>

            <ConfirmDialog
                isOpen={showConfirm}
                title="Xác nhận lưu kết quả thi"
                description="Bạn có chắc chắn muốn lưu các kết quả thi này?"
                onClose={() => setShowConfirm(false)}
                onConfirm={handleSaveResults}
            />
        </div>
    )
}